import {
  CalcType,
  ConfigurationId,
  PerformanceView,
  PerformanceViewOption,
  ProductData,
  RatingsCellData,
  SuppressDetails,
  ToggleProps,
  FundNameData,
  FundShareClassId,
  PpssCardItem,
  PpssCard,
  PpssCardSection,
  FundNameNjBestData,
} from '@types';
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ValueGetterParams,
  SuppressKeyboardEventParams,
} from '@ag-grid-community/core';
import { HeaderRendererComponent } from '@frk/eds-components';
import { Identifiers, PerformanceDetail, Yields } from '@models';
import {
  getCached1YearAgoStd,
  getCached6MonthsAgoStd,
} from '@utils/il8n/moment-il8n';

import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@shared/translate/translate.service';
import { createDateComparator, floatComparator } from '@utils/sort-utils';
import get from 'lodash/get';
import merge from 'lodash/merge';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import moment from 'moment';
import { parseNumber } from '@utils/text/number-utils';
import {
  API_DATE_FORMAT,
  EMDASH,
  N_A,
  PRODUCT_TYPE_ETF,
} from '@products/utils/constants/product.constants';
import { PerformanceComponent } from '@products/fund-listing/ag-grid-helpers/group-headers/performance-group-header/performance-group-header.component';
import { SiteConfigService } from '@services/site-config.service';
import { Logger } from '@utils/logger';
import { hasValue } from '@products/utils/mappers/mapper.utils';
import { FavoritesService } from '@products/services/favorites.service';
import { DOCUMENT } from '@angular/common';
import { PpssPerformanceComponent } from '../ppss/ppss-performance/ppss-performance.component';
import { PpssYieldsComponent } from '../ppss/ppss-yields/ppss-yields.component';
import { PpssRatingsComponent } from '../ppss/ppss-ratings/ppss-ratings.component';
import { PpssFundidentifiersComponent } from '../ppss/ppss-fundidentifiers/ppss-fundIdentifiers.component';
import { getNameForSorting } from '@utils/sort-utils';

const logger = Logger.getLogger('AgGridHelperService');

@Injectable({
  providedIn: 'root',
})
export class AgGridHelperService {
  private configName: ConfigurationId;

  public calcTypeConfiguration: Record<
    ConfigurationId,
    ToggleProps | string
  > = {
    [ConfigurationId.CEF]: {
      labelKey: CalcType.NAV_MARKET_PRICE,
      options: [
        {
          label: 'products.nav',
          value: 'NAV',
        },
        {
          label: 'products.market-price',
          value: 'MARKET_PRICE',
        },
      ],
      selected: 'NAV',
      type: 'toggle',
    },
    [ConfigurationId.SMA]: {
      labelKey: CalcType.PURE_GROSS_NET,
      options: [
        {
          label: 'products.net',
          value: 'NET',
        },
        {
          label: 'products.gross',
          value: 'GROSS',
        },
      ],
      selected: 'GROSS',
      applicableValues: {
        GROSS: ['PUREGROSS', 'GROSS'],
      },
      type: 'toggle',
    },
    [ConfigurationId.MF]: {
      labelKey: CalcType.SALES_CHARGES,
      options: [
        {
          label: 'products.off',
          value: 'NET',
        },
        {
          label: 'products.on',
          value: 'POP',
        },
      ],
      selected: 'NET',
      type: 'switch',
    },
    [ConfigurationId.MMF]: {
      labelKey: CalcType.SALES_CHARGES,
      options: [
        {
          label: 'products.off',
          value: 'NET',
        },
        {
          label: 'products.on',
          value: 'POP',
        },
      ],
      selected: 'NET',
      type: 'switch',
    },
    [ConfigurationId.ETF]: {
      labelKey: CalcType.NAV_MARKET_PRICE,
      options: [
        {
          label: 'products.nav',
          value: 'NAV',
        },
        {
          label: 'products.market-price',
          value: 'MARKET_PRICE',
        },
      ],
      selected: 'NAV',
      type: 'toggle',
    },
    // NGC-7688: Remove the Switch for Institutional Mutual Funds - Compliance comment
    [ConfigurationId.TIF]: 'NET',
    [ConfigurationId.GW]: 'NET',
    [ConfigurationId.US]: 'NET',
    [ConfigurationId.VIP]: 'NET',
    [ConfigurationId._529]: {
      labelKey: CalcType.SALES_CHARGES,
      options: [
        {
          label: 'products.off',
          value: 'NET',
        },
        {
          label: 'products.on',
          value: 'POP',
        },
      ],
      selected: 'NET',
      type: 'switch',
    },
    [ConfigurationId.IF]: {
      labelKey: CalcType.SALES_CHARGES,
      options: [
        {
          label: 'products.off',
          value: 'NET',
        },
        {
          label: 'products.on',
          value: 'POP',
        },
      ],
      selected: 'NET',
      type: 'switch',
    },
  };

  constructor(
    private translateService: TranslateService,
    private siteConfig: SiteConfigService,
    private favoritesService: FavoritesService,
    @Inject(DOCUMENT) readonly documentRef: Document
  ) {
    // NGC-7670: NextGen Singapore - Add Sales Charge Toggle for Mutual Funds
    if (this.siteConfig.getPdsCountry() === 'SG') {
      this.calcTypeConfiguration[ConfigurationId.MF] = {
        labelKey: CalcType.SALES_CHARGES,
        options: [
          {
            label: 'products.off',
            value: 'NET',
          },
          {
            label: 'products.on',
            value: 'AACR',
          },
        ],
        selected: 'NET',
        type: 'switch',
      };

      this.calcTypeConfiguration[ConfigurationId.MMF] = 'NET';
    }
  }

  set configurationName(configurationName: ConfigurationId) {
    this.configName = configurationName;
  }

  get configurationName(): ConfigurationId {
    return this.configName;
  }

  private static suppressDefaultTabbing(
    params: SuppressKeyboardEventParams
  ): boolean {
    const pressedKey = params.event;
    if (pressedKey.code === 'Tab' || pressedKey.key === 'Tab') {
      const buttonsInCell = (pressedKey.target as Element)
        .closest('.ag-cell')
        .querySelectorAll('button, [href], svg, eds-icon');
      // cond 1 : if no buttons, then default navigation to next cell
      // cond 2 : if already at the last button in cell then default navigation to next cell
      // cond 3 : if at first button and we press Shift + tab default navigation to previous cell.
      if (
        buttonsInCell.length === 0 ||
        (pressedKey.shiftKey === false &&
          pressedKey.target === buttonsInCell[buttonsInCell.length - 1]) ||
        (pressedKey.shiftKey === true && pressedKey.target === buttonsInCell[0])
      ) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  }

  public getConfigurationNameForPlacement() {
    return this.configName?.replace(/-|\s/g, '');
  }

  // ***** AG-Grid Column Definitions Starts *****
  // Below definitions have default properties for each column.
  // Any modifications to each property can be overridden accordingly.

  getFundCheckboxColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CHECK-BOX',
      headerComponentFramework: HeaderRendererComponent,
      checkboxSelection: true,
      suppressMovable: true,
      minWidth: 65,
      maxWidth: 65,
    };

    return merge(initialColDef, alteredColDef);
  }

  getFavFundColDef(
    parentComponent?:
      | PpssPerformanceComponent
      | PpssFundidentifiersComponent
      | PpssYieldsComponent
      | PpssRatingsComponent,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'FAV-FUND',
      headerName: this.translateService.instant(
        'products.fav-icon-column-header'
      ),
      headerComponentFramework: HeaderRendererComponent,
      minWidth: 40,
      maxWidth: 60,
      sortable: true,
      comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
        const isFavFundA = nodeA.data.isFavoriteFund;
        const isFavFundB = nodeB.data.isFavoriteFund;
        return isFavFundB === isFavFundA ? 0 : isFavFundA ? 1 : -1;
      },
      sort: 'desc',
      cellRenderer: 'favFundCellRendererComponent',
      cellRendererParams: { parentComponent, print: { minWidth: 25 } },
      hide: !this.favoritesService.isFavouriteEnabled(),
      onCellClicked: (event) => {
        // Disable Focus on cell-click
        if (this.documentRef.activeElement instanceof HTMLElement) {
          this.documentRef.activeElement.blur();
        }
      },
    };

    return merge(initialColDef, alteredColDef);
  }

  getFundNameColDef(
    isSiteIntl: boolean,
    showSalesCharges: boolean = false,
    alteredColDef?: ColDef,
    configurationName?: ConfigurationId,
    showPerformanceAndMaintananceCharges?: boolean,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'FUND-NAME',
      headerName: this.translateService.instant('pds.FUND_NAME'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: false },
      cellRenderer: 'fundNameCellRendererComponent',
      cellRendererParams: {
        isSiteIntl,
        showSalesCharges,
        showPerformanceAndMaintananceCharges,
      },
      suppressMovable: true,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.fundName;
      },
      sortable: true,
      comparator: (
        value1: string,
        value2: string,
        nodeA,
        nodeB,
        isInverted
      ) => {
        if (configurationName === ConfigurationId._529) {
          return this.sortFor529(value1, value2, true);
        } else if (configurationName === ConfigurationId.SMA) {
          // to apply custom sort when string contains 'number-number' pattern
          if (value1.match(/\d+-\d+/g) || value2.match(/\d+-\d+/g)) {
            return this.sortFor529(value1, value2, false);
          } else {
            return this.commonSortOnName(value1, value2);
          }
        } else {
          return this.commonSortOnName(value1, value2);
        }
      },
      minWidth: 154,
      autoHeight: true,
      wrapText: true,
      flex: 1,
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return AgGridHelperService.suppressDefaultTabbing(params);
      },
    };

    return merge(initialColDef, alteredColDef);
  }

  getFundNameNjBestColDef(
    isSiteIntl: boolean,
    showSalesCharges: boolean = false,
    alteredColDef?: ColDef,
    configurationName?: ConfigurationId,
    showPerformanceAndMaintananceCharges?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'FUND-NAME',
      headerName: this.translateService.instant('products.ppss-portfolio-name'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: false },
      cellRenderer: 'fundNameNjBestCellRendererComponent',
      cellRendererParams: {
        isSiteIntl,
        showSalesCharges,
        showPerformanceAndMaintananceCharges,
      },
      suppressMovable: true,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.fundName;
      },
      sortable: true,
      comparator: (
        value1: string,
        value2: string,
        nodeA,
        nodeB,
        isInverted
      ) => {
        return this.sortFor529(value1, value2, true);
      },
      minWidth: 154,
      autoHeight: true,
      wrapText: true,
      flex: 1,
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return AgGridHelperService.suppressDefaultTabbing(params);
      },
    };

    return merge(initialColDef, alteredColDef);
  }

  commonSortOnName(value1: string, value2: string): number {
    const dashes = ['\u2014', '&#8212;', '-', '—', ''];
    // depending on direction, make sure dashes or empty always last / bottom
    if (dashes.includes(value1)) {
      // dashes or empty always last
      return 1;
    }
    if (dashes.includes(value2)) {
      // dashes or empty always last
      return -1;
    }
    // string compare
    return value1.localeCompare(value2);
  }

  sortFor529(value1: string, value2: string, is529Doc?: boolean): number {
    const editedValue1 = is529Doc ? getNameForSorting(value1) : value1;
    const editedValue2 = is529Doc ? getNameForSorting(value2) : value2;
    const reA = /[^a-zA-Z]/g;
    const reN = /[^0-9]/g;
    const aA = editedValue1.replace(reA, '');
    const bA = editedValue2.replace(reA, '');
    if (aA === bA) {
      const aN = parseInt(editedValue1.replace(reN, ''), 10);
      const bN = parseInt(editedValue2.replace(reN, ''), 10);
      return aN === bN ? 0 : aN > bN ? 1 : -1;
    } else {
      return aA > bA ? 1 : -1;
    }
  }

  public getFundDetailToDisplay(
    product: ProductData,
    isSiteIntl: boolean,
    showSecondSuffix?: boolean
  ): FundNameData {
    const shareClassSuffix = this.getShareClassSuffix(product);
    const identifierSuffix = this.getIdentifierSuffix(product);
    const fundInfo: FundNameData = {
      fundName: product.fundName,
      suffix: null,
      highlightSuffix: false,
    };
    if (
      isSiteIntl &&
      product.productType === PRODUCT_TYPE_ETF &&
      identifierSuffix
    ) {
      fundInfo.suffix = identifierSuffix;
      fundInfo.highlightSuffix = true;
    } else if (isSiteIntl && shareClassSuffix) {
      fundInfo.suffix = shareClassSuffix;
    } else if (!isSiteIntl && identifierSuffix) {
      fundInfo.suffix = identifierSuffix;
      fundInfo.highlightSuffix = true;
      if (shareClassSuffix && showSecondSuffix) {
        fundInfo.secondSuffix = shareClassSuffix;
      }
    }
    return fundInfo;
  }

  public getFundDetailNjBest(product: ProductData): FundNameNjBestData {
    const identifier: Identifiers = get(product, 'shareClass', [])?.identifiers;
    const identifierSuffix = this.getIdentifierSuffix(product);
    const fundInfo: FundNameNjBestData = {
      // add identifier based on bloomreach configuration
      fundName: identifierSuffix
        ? product.fundName + ' - ' + identifierSuffix
        : product.fundName,
      row1: `${this.translateService.instant('products.ppss-portfolio-num')}: ${
        identifier?.taid
      } | ${this.translateService.instant('products.ppss-cusip')}: ${
        identifier?.cusip
      }`,
      row2: `${this.translateService.instant(
        'products.ppss-total-annual-op-expenses'
      )}: ${product?.shareClass?.charges?.annualCharge}%`,
    };
    return fundInfo;
  }

  private getShareClassSuffix(product: ProductData): string {
    return product.shareClass?.shareClassName &&
      !product.shareClass.isSingleShareClass
      ? product.shareClass.shareClassName
      : null;
  }

  private getIdentifierSuffix(product: ProductData): string {
    const identifierKey = product.productTaxonomy
      ? this.siteConfig.getDefaultIdentifierKey(product.productTaxonomy)
      : null;
    const identifierSuffix: string =
      identifierKey in product.shareClass.identifiers &&
      hasValue(product.shareClass.identifiers, identifierKey)
        ? product.shareClass.identifiers?.[identifierKey]
        : null;
    return identifierSuffix;
  }

  getShareClassNameColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'SHARECLASS-NAME',
      headerName: this.translateService.instant('products.ppss-share-class'),
      headerComponentParams: {
        isAlignRight: false,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}ShareClassPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      suppressMovable: true,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        if (product.shareClass.isSingleShareClass) {
          return EMDASH;
        }
        return product.shareClass?.shareClassName;
      },
      minWidth: 80,
      maxWidth: 108,
      wrapText: false,
      cellStyle: { 'white-space': 'normal' },
    };

    return merge(initialColDef, alteredColDef);
  }

  getQuickLinksColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'QUICK-LINKS',
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.isSoftLaunch ? '' : '...';
      },
      width: 60,
      suppressMovable: true,
    };

    return merge(initialColDef, alteredColDef);
  }

  getNavValueColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'NAVVALUE',
      headerName: this.translateService.instant('products.ppss-nav-value'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}NavPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      suppressMovable: true,
      // NB: keep valueGetter function for use by card view
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.navValue;
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.navValue;
      },
      sortable: true,
      comparator: floatComparator,
      width: 125,
      cellClass: ['ag-cell-frk--highlightedSecondary', 'ag-cell-frk--monetary'],
      cellRendererParams: { print: { minWidth: 50 } },
    };

    return merge(initialColDef, alteredColDef);
  }

  get52WeekHighColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: '52WEEKHIGH',
      headerName: this.translateService.instant('products.ppss-52week-high'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}52WeekHighPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      suppressMovable: true,
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return `<span>${
          product.shareClass?.nav?.highestNavValueIn52Weeks
        }</span> 
        <p class="small text-grey1">
        ${this.translateService.instant('products.on')} ${
          product.shareClass?.nav?.highestNavValueDateIn52Weeks
        }</p>`;
      },
      sortable: true,
      // width: 125,
      cellClass: ['ag-cell-frk--highlightedSecondary', 'ag-cell-frk--monetary'],
    };

    return merge(initialColDef, alteredColDef);
  }

  get52WeekLowColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: '52WEEKLOW',
      headerName: this.translateService.instant('products.ppss-52week-low'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}52WeekLowPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      suppressMovable: true,
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return `<span>${product.shareClass?.nav?.lowestNavValueIn52Weeks}</span>
        <p class="small text-grey1"> 
        ${this.translateService.instant('products.on')} ${
          product.shareClass?.nav?.lowestNavValueDateIn52Weeks
        }<p>`;
      },
      sortable: true,
      // width: 125,
      cellClass: ['ag-cell-frk--highlightedSecondary', 'ag-cell-frk--monetary'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getPortfolioNumColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'PORTFOLIONUM',
      headerName: this.translateService.instant('products.ppss-portfolio-num'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}PortfolioNumPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      suppressMovable: true,
      // NB: keep valueGetter function for use by card view
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers?.taid;
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers?.taid;
      },
      sortable: true,
      comparator: floatComparator,
      // width: 125,
      cellClass: ['ag-cell-frk--highlightedSecondary', 'ag-cell-frk--monetary'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getMarketPriceValueColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'MARKETPRICEVALUE',
      headerName: this.translateService.instant('pds.MARKET_VALUE'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}MarketPricePPSS`, // To-Do: check key once
      },
      headerComponentFramework: HeaderRendererComponent,
      suppressMovable: true,
      // NB: keep valueGetter function for use by card view
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.marketPriceValue;
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.marketPriceValue;
      },
      sortable: true,
      comparator: floatComparator,
      width: 125,
      cellClass: ['ag-cell-frk--highlightedSecondary', 'ag-cell-frk--monetary'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getNavChangeValueColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'NAVCHANGEVALUE',
      headerName: this.translateService.instant('pds.DAILY_NAV_CHANGE'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}NavChangePPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.navChangePercentage;
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;

        return product.shareClass?.nav?.navChangeValue
          ? `
                    <p class="spacing--none">${product.shareClass?.nav?.navChangePercentage}</p>
                    <p class="spacing--none">${product.shareClass?.nav?.navChangeValue}</p>
                `
          : EMDASH;
      },
      cellRendererParams: { print: { minWidth: 50 } },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 115,
      autoHeight: true,
      cellClass: (params) => {
        const cellClasses = [
          'ag-cell-frk--highlightedSecondary',
          'ag-cell-frk--monetary',
          'ag-cell-frk--percentage',
        ];
        const floatRegex = /[+-]?[0-9]*[.]?[0-9]+/g;
        const product: ProductData = params.data;
        const sign = product.shareClass?.nav?.navChangeValue
          ? Math.sign(
              parseFloat(
                product.shareClass?.nav?.navChangeValue
                  ?.match(floatRegex)
                  ?.join('')
              )
            )
          : 0;
        if (sign === 1) {
          cellClasses.push('ag-cell-frk--gain');
        } else if (sign === -1) {
          cellClasses.push('ag-cell-frk--loss');
        } else {
          cellClasses.push('ag-cell-frk--static');
        }
        return cellClasses;
      },
    };

    return merge(initialColDef, alteredColDef);
  }

  getNavChangePercentageColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'NAVCHANGEPERCENTAGE',
      headerName: this.translateService.instant('pds.DAILY_NAV_CHANGE'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}NavChangePPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.navChangePercentage;
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;

        return product.shareClass?.nav?.navChangeValue
          ? `
                    <p class="spacing--none">${product.shareClass?.nav?.navChangePercentage}</p>
                    <p class="spacing--none">${product.shareClass?.nav?.navChangeValue}</p>
                `
          : product.shareClass?.nav?.navChangePercentage
          ? `<p class="spacing--none">${product.shareClass?.nav?.navChangePercentage}</p>`
          : EMDASH;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 115,
      autoHeight: true,
      cellClass: (params) => {
        const cellClasses = [
          'ag-cell-frk--highlightedSecondary',
          'ag-cell-frk--monetary',
          'ag-cell-frk--percentage',
        ];
        const floatRegex = /[+-]?[0-9]*[.]?[0-9]+/g;
        const product: ProductData = params.data;
        const sign = product.shareClass?.nav?.navChangeValue
          ? Math.sign(
              parseFloat(
                product.shareClass?.nav?.navChangeValue
                  ?.match(floatRegex)
                  ?.join('')
              )
            )
          : 0;
        if (sign === 1) {
          cellClasses.push('ag-cell-frk--gain');
        } else if (sign === -1) {
          cellClasses.push('ag-cell-frk--loss');
        } else {
          cellClasses.push('ag-cell-frk--static');
        }
        return cellClasses;
      },
    };

    return merge(initialColDef, alteredColDef);
  }

  getMarketPriceChangeValueColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'MARKETPRICECHANGEVALUE',
      headerName: this.translateService.instant(
        'pds.MARKET_PRICE_VALUE_CHANGE_PCT'
      ),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}MarketPriceChangePPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.marketPriceChangePercentage;
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;

        return product.shareClass?.nav?.marketPriceChangeValue
          ? `
                    <p class="spacing--none">${product.shareClass?.nav?.marketPriceChangePercentage}</p>
                    <p class="spacing--none">${product.shareClass?.nav?.marketPriceChangeValue}</p>
                `
          : EMDASH;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 115,
      autoHeight: true,
      cellClass: (params) => {
        const cellClasses = [
          'ag-cell-frk--highlightedSecondary',
          'ag-cell-frk--monetary',
          'ag-cell-frk--percentage',
        ];
        const floatRegex = /[+-]?[0-9]*[.]?[0-9]+/g;
        const product: ProductData = params.data;
        const sign = product.shareClass?.nav?.marketPriceChangeValue
          ? Math.sign(
              parseFloat(
                product.shareClass?.nav?.marketPriceChangeValue
                  ?.match(floatRegex)
                  ?.join('')
              )
            )
          : 0;
        if (sign === 1) {
          cellClasses.push('ag-cell-frk--gain');
        } else if (sign === -1) {
          cellClasses.push('ag-cell-frk--loss');
        } else {
          cellClasses.push('ag-cell-frk--static');
        }
        return cellClasses;
      },
    };

    return merge(initialColDef, alteredColDef);
  }

  getNavYTDReturnColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'NAVYTDRETURN',
      headerName: this.translateService.instant('products.nav-ytd'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}NavYtdPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      cellRendererParams: { print: { minWidth: 45 } },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.navYearToDateReturn;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: [
        'ag-cell-frk--highlightedSecondary',
        'ag-cell-frk--percentage',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getNavYTDReturnNjBestColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-MONTHLY_NAVYTDRETURN',
      headerName: this.translateService.instant('products.nav-ytd'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}NavYtdPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.navYearToDateReturn;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--percentage'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getMarketPriceYTDReturnColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'MARKETPRICEYTDRETURN',
      headerName: this.translateService.instant('products.market-price-ytd'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}MarketPriceYtdPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.marketPriceYearToDateReturn
          ? product.shareClass?.nav?.marketPriceYearToDateReturn
          : EMDASH;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: [
        'ag-cell-frk--highlightedSecondary',
        'ag-cell-frk--percentage',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDailyVolumeColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'DAILY-VOLUME',
      headerName: this.translateService.instant('products.daily-volume'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}DailyVolumePPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.dailyVolume;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 110,
      cellClass: [
        'ag-cell-frk--highlightedSecondary',
        'ag-cell-frk--percentage',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getPremiumDiscountColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'PREMIUM-DISCOUNT',
      headerName: this.translateService.instant('products.Premium-Discount'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}PremiumDiscountPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.premiumDiscountPct;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 100,
      cellClass: [
        'ag-cell-frk--highlightedSecondary',
        'ag-cell-frk--percentage',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualMonthly1YrColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef,
    isNjbest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-MONTHLY-1YR',
      headerName: this.translateService.instant('pds.AVG_ANNUAL_RET_1YR'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.MONTHLY,
              params,
              'avgAnnual1YearReturn'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualMonthly3YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-MONTHLY-3YR',
      headerName: this.translateService.instant('pds.AVG_ANNUAL_RET_3YR'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'avgAnnual3YearReturn'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
      cellRendererParams: { print: { minWidth: 50 } },
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualMonthly5YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-MONTHLY-5YR',
      headerName: this.translateService.instant('pds.AVG_ANNUAL_RET_5YR'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'avgAnnual5YearReturn'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
      cellRendererParams: { print: { minWidth: 35 } },
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualMonthly7YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-MONTHLY-7YR',
      headerName: this.translateService.instant('products.ppss-sevenYear'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'avgAnnual7YearReturn'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualMonthly10YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-MONTHLY-10YR',
      headerName: this.translateService.instant('pds.AVG_ANNUAL_RET_10YR'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'avgAnnual10YearReturn'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualMonthlyInceptionColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef,
    showInceptionDate = true
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-MONTHLY-INCEPTION',
      headerName: this.translateService.instant('products.sinceInception'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}PerfInceptionPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        if (
          this.isSuppressForInceptionValid(
            suppressDetails,
            product.shareClass?.performanceInceptionDateStd
          )
        ) {
          return EMDASH;
        } else {
          return this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'avgAnnualSinceInceptionReturn'
          );
        }
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        let inceptionTemplate: string;
        const inceptionDateTemplate = `<p class="small text-grey1">
                ${product.shareClass?.performanceInceptionDate}
            </p>`;
        if (
          this.isSuppressForInceptionValid(
            suppressDetails,
            product.shareClass?.performanceInceptionDateStd
          )
        ) {
          inceptionTemplate = `
                        <span>
                            ${EMDASH}
                        </span>
                    `;
        } else {
          const avgAnnualSinceInceptionReturn = this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'avgAnnualSinceInceptionReturn'
          );
          inceptionTemplate = `
                        <span>
                            ${avgAnnualSinceInceptionReturn}
                        </span>
                    `;
        }
        if (showInceptionDate) {
          inceptionTemplate += inceptionDateTemplate;
        }
        return inceptionTemplate;
      },
      cellRendererParams: { print: { minWidth: 70 } },
      width: 135,
      cellClass: [
        'ag-cell-frk--highlightedTertiary',
        'ag-cell-frk--numeric',
        'ag-cell-frk--date',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualInceptionDateColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-INCEPTION',
      headerName: this.translateService.instant('products.fund-inception-date'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}FundInceptionDate`,
      },
      sortable: true,
      suppressMovable: true,
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.performanceInceptionDate;
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.performanceInceptionDateStd;
      },
      comparator: createDateComparator(API_DATE_FORMAT),
      width: 135,
      cellClass: ['ag-cell-frk--highlightedTertiary'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualQuarterly1YrColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-QUARTERLY-1YR',
      headerName: this.translateService.instant('pds.AVG_ANNUAL_RET_1YR'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.QUARTERLY,
              params,
              'avgAnnual1YearReturn'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualQuarterly3YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-QUARTERLY-3YR',
      headerName: this.translateService.instant('pds.AVG_ANNUAL_RET_3YR'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'avgAnnual3YearReturn'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
      cellRendererParams: { print: { minWidth: 50 } },
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualQuarterly5YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-QUARTERLY-5YR',
      headerName: this.translateService.instant('pds.AVG_ANNUAL_RET_5YR'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'avgAnnual5YearReturn'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
      cellRendererParams: { print: { minWidth: 35 } },
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualQuarterly7YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-QUARTERLY-7YR',
      headerName: this.translateService.instant('products.sevenYear'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'avgAnnual7YearReturn'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualQuarterly10YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-QUARTERLY-10YR',
      headerName: this.translateService.instant('pds.AVG_ANNUAL_RET_10YR'),
      headerComponentParams: { isAlignRight: true },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'avgAnnual10YearReturn'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvgAnnualQuarterlyInceptionColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef,
    showInceptionDate = true
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG_ANNUAL-QUARTERLY-INCEPTION',
      headerName: this.translateService.instant('products.sinceInception'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}PerfInceptionPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        if (
          this.isSuppressForInceptionValid(
            suppressDetails,
            product.shareClass?.performanceInceptionDateStd
          )
        ) {
          return EMDASH;
        } else {
          return this.getPerformanceData(
            this.configurationName,
            PerformanceView.QUARTERLY,
            params,
            'avgAnnualSinceInceptionReturn'
          );
        }
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        let inceptionTemplate: string;
        const inceptionDateTemplate = `<p class="small text-grey1">
                ${product.shareClass?.performanceInceptionDate}
            </p>`;
        if (
          this.isSuppressForInceptionValid(
            suppressDetails,
            product.shareClass?.performanceInceptionDateStd
          )
        ) {
          inceptionTemplate = `
                        <span>
                            ${EMDASH}
                        </span>
                    `;
        } else {
          const avgAnnualSinceInceptionReturn = this.getPerformanceData(
            this.configurationName,
            PerformanceView.QUARTERLY,
            params,
            'avgAnnualSinceInceptionReturn'
          );
          inceptionTemplate = `
                        <span>
                            ${avgAnnualSinceInceptionReturn}
                        </span>
                    `;
        }
        if (showInceptionDate) {
          inceptionTemplate += inceptionDateTemplate;
        }
        return inceptionTemplate;
      },
      cellRendererParams: { print: { minWidth: 70 } },
      width: 135,
      cellClass: [
        'ag-cell-frk--highlightedTertiary',
        'ag-cell-frk--numeric',
        'ag-cell-frk--date',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getExpenseRatioColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'EXPENSERATIO',
      headerName: this.translateService.instant('products.expense-ratio'),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}ExpenseRatioPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      suppressMovable: true,
      cellRenderer: 'expenseRatioCellRendererComponent',
      cellRendererParams: { print: { minWidth: 90 } },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.charges?.expenseRatioGross;
      },
      sortable: true,
      comparator: floatComparator,
      minWidth: 130,
      maxWidth: 130,
      cellClass: ['ag-cell-frk--highlightedSecondary', 'ag-cell-frk--numeric'],
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return AgGridHelperService.suppressDefaultTabbing(params);
      },
    };

    return merge(initialColDef, alteredColDef);
  }

  getFundDocumentColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'FUND-DOCUMENT',
      headerName: this.translateService.instant('products.fact-sheet'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: false },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.documents?.find((document) => document.showOnPpss);
      },
      width: 90,
      sortable: false,
      suppressMovable: true,
      cellClass: ['ag-cell-frk--highlightedTertiary'],
      cellRenderer: 'fundDocumentCellRendererComponent',
      cellRendererParams: { print: { hide: true } },
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return AgGridHelperService.suppressDefaultTabbing(params);
      },
    };
    return merge(initialColDef, alteredColDef);
  }

  getTotalAnnualOperatingExpensesColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'TOT-ANNUAL-OP-EXPENSES',
      headerName: this.translateService.instant(
        'products.total-annual-op-expenses'
      ),
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}AnnualOperatingExpensesPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.charges?.annualCharge;
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.charges?.annualCharge;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 130,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };
    return merge(initialColDef, alteredColDef);
  }

  getDistributionRateColDef(
    alteredColDef?: ColDef,
    calcType: string = null,
    isSiteIntl: boolean = false,
    productType: ConfigurationId = ConfigurationId.MF
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISTRIBUTION-RATE',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}DistributionRatePPSS`,
      },
      headerValueGetter: () => {
        const selectedCalcType =
          !isSiteIntl && productType === ConfigurationId.MF
            ? 'NET' // NGC-10993: always 'NET' in case of US-MF.
            : calcType || this.getSelectedCalcType(this.configurationName);
        const salesChargeLabelKey =
          selectedCalcType === 'POP'
            ? 'products.distribution-rate-pop'
            : selectedCalcType === 'MARKET_PRICE'
            ? 'products.distribution-rate-market-price'
            : 'products.distribution-rate-nav';
        return this.translateService.instant(salesChargeLabelKey);
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        const selectedCalcType =
          !isSiteIntl && productType === ConfigurationId.MF
            ? 'NET' // NGC-10993: always 'NET' in case of US-MF.
            : calcType || this.getSelectedCalcType(this.configurationName);
        const prop =
          selectedCalcType === 'POP' || selectedCalcType === 'MARKET_PRICE'
            ? 'distributionRateAtPOP'
            : 'distributionRateAtNAV';
        return product.shareClass?.nav && prop in product.shareClass.nav
          ? product.shareClass.nav[prop]
          : EMDASH;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      minWidth: 200,
      maxWidth: 200,
      cellClass: [
        'ag-cell-frk--highlightedSecondary',
        'ag-cell-frk--percentage',
      ],
      cellRendererParams: { print: { minWidth: 110 } },
    };

    return merge(initialColDef, alteredColDef);
  }

  public getSelectedCalcType(configurationName?: ConfigurationId): string {
    const calcType: ToggleProps | string = this.calcTypeConfiguration[
      configurationName ? configurationName : this.configurationName
    ];
    let selected = '';
    if (typeof calcType === 'string') {
      selected = calcType;
    } else if (calcType) {
      selected = (calcType as ToggleProps).selected;
    }
    return selected;
  }

  public getSelectedCalcTypeValues(
    configurationName?: ConfigurationId
  ): string[] {
    const calcType: ToggleProps | string = this.calcTypeConfiguration[
      configurationName ? configurationName : this.configurationName
    ];
    let selected = [];
    if (typeof calcType === 'string') {
      selected = [calcType];
    } else if (calcType) {
      const selectedCalcType = calcType as ToggleProps;
      const selectedCalcTypeVal = calcType.selected;
      selected =
        selectedCalcType.applicableValues &&
        selectedCalcTypeVal in selectedCalcType.applicableValues
          ? selectedCalcType.applicableValues[selectedCalcTypeVal]
          : [selectedCalcTypeVal];
    }
    return selected;
  }

  get30DayStandardizedYieldWithoutWaiverColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: '30DAYSTDYIELDWITHOUTWAIVER',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}30DayStdYieldWithoutWaiverPPSS`,
      },
      headerName: `${this.translateService.instant(
        'pds.STD_SEC_YIELD_30_DAY_WO_WAIVER'
      )}`,
      valueGetter: (params) => {
        const yields: Yields = params.data.shareClass?.yields;
        return yields?.secYieldThrtyDayWoWaiver
          ? yields.secYieldThrtyDayWoWaiver
          : N_A;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      minWidth: 200,
      maxWidth: 200,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
      cellRendererParams: { print: { minWidth: 140 } },
    };

    return merge(initialColDef, alteredColDef);
  }

  get30DayStandardizedYieldWithWaiverColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: '30DAYSTDYIELDWITHWAIVER',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}30DayStdYieldWithWaiverPPSS`,
      },
      headerName: `${this.translateService.instant(
        'pds.STD_SEC_YIELD_30_DAY'
      )}`,
      valueGetter: (params) => {
        const yields: Yields = params.data.shareClass?.yields;
        return yields?.secYieldThrtyDay ? yields.secYieldThrtyDay : N_A;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      minWidth: 200,
      maxWidth: 200,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
      cellRendererParams: { print: { minWidth: 140 } },
    };

    return merge(initialColDef, alteredColDef);
  }

  get7DayCurrentYieldWithoutWaiverColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: '7DAYCURRENTYIELDWITHOUTWAIVER',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        isAlignRight: false,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}7DayCurrentYieldWithoutWaiverPPSS`,
      },
      headerName: `${this.translateService.instant(
        'pds.CURR_YIELD_7DAY_WO_WAIVER'
      )}`,
      valueGetter: (params) => {
        const yields: Yields = params.data.shareClass?.yields;
        return yields?.currentYield7DayWithoutWaiver
          ? yields.currentYield7DayWithoutWaiver
          : N_A;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      minWidth: 150,
      maxWidth: 200,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  get7DayCurrentYieldWithWaiverColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: '7DAYCURRENTYIELDWITHWAIVER',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        isAlignRight: false,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}7DayCurrentYieldWithWaiverPPSS`,
      },
      headerName: `${this.translateService.instant('pds.CURR_YIELD_7DAY')}`,
      valueGetter: (params) => {
        const yields: Yields = params.data.shareClass?.yields;
        return yields?.currentYield7Day ? yields.currentYield7Day : N_A;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      minWidth: 150,
      maxWidth: 200,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  get7DayEffectiveYieldWithoutWaiverColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: '7DAYEFFECTIVEYIELDWITHOUTWAIVER',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        isAlignRight: false,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}7DayEffectiveYieldWithoutWaiverPPSS`,
      },
      headerName: `${this.translateService.instant(
        'pds.EFFECTIVE_YIELD_7_DAY_WO_WAIVER'
      )}`,
      valueGetter: (params) => {
        const yields: Yields = params.data.shareClass?.yields;
        return yields?.effectiveYield7DayWithoutWaiver
          ? yields.effectiveYield7DayWithoutWaiver
          : N_A;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      minWidth: 150,
      maxWidth: 200,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  get7DayEffectiveYieldWithWaiverColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: '7DAYEFFECTIVEYIELDWITHWAIVER',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        isAlignRight: false,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}7DayEffectiveYieldWithWaiverPPSS`,
      },
      headerName: `${this.translateService.instant(
        'pds.EFFECTIVE_YIELD_7_DAY'
      )}`,
      valueGetter: (params) => {
        const yields: Yields = params.data.shareClass?.yields;
        return yields?.effectiveYield7Day ? yields.effectiveYield7Day : N_A;
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      minWidth: 150,
      maxWidth: 200,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getMorningStarCategoryColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'MORNINGSTAR-CATEGORY',
      headerName: this.translateService.instant('pds.MS_CATEGORY'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `PPSS:MorningStarCategoyPPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass.ratings?.morningStarCategory
          ? product.shareClass.ratings?.morningStarCategory
          : EMDASH;
      },
      sortable: true,
      suppressMovable: true,
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
    };

    return merge(initialColDef, alteredColDef);
  }

  getMorningStarOverallRatingColDef(
    isSiteIntl: boolean,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'MORNINGSTAR-OVERALL',
      headerName: isSiteIntl
        ? this.translateService.instant('mstar-rating')
        : this.translateService.instant('pds.MS_RATING_OVERALL'),
      headerComponentParams: {
        isAlignRight: false,
        footnotePlacement: `PPSS:RatingsPPSS`,
      },
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass.ratings?.morningStarRatingOverall;
      },
      cellRenderer: 'ratingsCellRenderComponent',
      cellRendererParams: {
        ratingsPath: 'shareClass.ratings.morningStarRatingOverall',
        hybridPath: 'shareClass.ratings.morningStarOverallHybrid',
        print: { minWidth: 90 },
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      minWidth: 200,
      cellClass: ['ag-cell-frk--highlightedSecondary'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getMorningStar3yrRatingColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'MORNINGSTAR-RATING3YR',
      headerName: this.translateService.instant('pds.MS_RATING_3YR'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass.ratings?.morningStarRating3yr;
      },
      cellRenderer: 'ratingsCellRenderComponent',
      cellRendererParams: {
        ratingsPath: 'shareClass.ratings.morningStarRating3yr',
        hybridPath: 'shareClass.ratings.morningStar3yrHybrid',
        numberOfFundsPath: 'shareClass.ratings.monthEndNoOfFunds3yr',
        print: { minWidth: 100 },
      },
      sortable: true,
      suppressMovable: true,
      minWidth: 135,
      maxWidth: 150,
      autoHeight: true,
      wrapText: true,
    };

    return merge(initialColDef, alteredColDef);
  }

  getMorningStar5yrRatingColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'MORNINGSTAR-RATING5YR',
      headerName: this.translateService.instant('pds.MS_RATING_5YR'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass.ratings?.morningStarRating5yr;
      },
      cellRenderer: 'ratingsCellRenderComponent',
      cellRendererParams: {
        ratingsPath: 'shareClass.ratings.morningStarRating5yr',
        hybridPath: 'shareClass.ratings.morningStar5yrHybrid',
        numberOfFundsPath: 'shareClass.ratings.monthEndNoOfFunds5yr',
        print: { minWidth: 100 },
      },
      sortable: true,
      suppressMovable: true,
      minWidth: 135,
      maxWidth: 150,
      autoHeight: true,
      wrapText: true,
    };

    return merge(initialColDef, alteredColDef);
  }

  getMorningStar10yrRatingColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'MORNINGSTAR-RATING10YR',
      headerName: this.translateService.instant('pds.MS_RATING_10YR'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass.ratings?.morningStarRating10yr;
      },
      cellRenderer: 'ratingsCellRenderComponent',
      cellRendererParams: {
        ratingsPath: 'shareClass.ratings.morningStarRating10yr',
        hybridPath: 'shareClass.ratings.morningStar10yrHybrid',
        numberOfFundsPath: 'shareClass.ratings.monthEndNoOfFunds10yr',
        print: { minWidth: 100 },
      },
      sortable: true,
      suppressMovable: true,
      minWidth: 150,
      maxWidth: 150,
      autoHeight: true,
      wrapText: true,
    };

    return merge(initialColDef, alteredColDef);
  }
  getCurrencyColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CURRENCY_COL',
      headerName: this.translateService.instant('pds.SHARE_CLASS_CURRENCY'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.shareClassCurrency;
      },
      width: 80,
      suppressMovable: true,
    };

    return merge(initialColDef, alteredColDef);
  }

  getInvestmentVehicleColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'INVESTMENT_VEHICLE',
      headerName: this.translateService.instant(
        'products.ppss-filter-INVESTMENT_VEHICLE'
      ),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.investmentVehicle;
      },
      width: 300,
      suppressMovable: true,
    };

    return merge(initialColDef, alteredColDef);
  }

  getApplicationPriceColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'APPLICATION_PRICE',
      headerName: this.translateService.instant('pds.APPLICATION_PRICE'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.popValue
          ? product.shareClass?.nav?.popValue
          : EMDASH;
      },
      width: 80,
      suppressMovable: true,
      headerComponentParams: {
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}AppPricePPSS`,
      },
    };

    return merge(initialColDef, alteredColDef);
  }

  getRedemptionPriceColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'REDEMPTION_PRICE',
      headerName: this.translateService.instant('pds.REDEMPTION_PRICE'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.nav?.redemptionPrice
          ? product.shareClass?.nav?.redemptionPrice
          : EMDASH;
      },
      width: 80,
      suppressMovable: true,
      headerComponentParams: {
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}RedPricePPSS`,
      },
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearMonthlyYtdColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-MONTHLY-YTD',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_YTD'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.MONTHLY,
              params,
              'cummulativeReturnYearToDate'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: [
        'ag-cell-frk--highlightedTertiary',
        'ag-cell-frk--numeric',
        'ag-cell-frk--monetary',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearMonthly1YrColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-MONTHLY-1YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.MONTHLY,
              params,
              'calendarYearReturn1Year'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearMonthly2YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-MONTHLY-2YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'calendarYearReturn2Year'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearMonthly3YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-MONTHLY-3YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'calendarYearReturn3Year'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearMonthly4YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-MONTHLY-4YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'calendarYearReturn4Year'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearMonthly5YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-MONTHLY-5YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'calendarYearReturn5Year'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearQuarterlyYtdColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-QUARTERLY-YTD',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_YTD'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.QUARTERLY,
              params,
              'cummulativeReturnYearToDate'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: [
        'ag-cell-frk--highlightedTertiary',
        'ag-cell-frk--numeric',
        'ag-cell-frk--monetary',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearQuarterly1YrColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-QUARTERLY-1YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.QUARTERLY,
              params,
              'calendarYearReturn1Year'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearQuarterly2YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-QUARTERLY-2YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'calendarYearReturn2Year'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearQuarterly3YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-QUARTERLY-3YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'calendarYearReturn3Year'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearQuarterly4YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-QUARTERLY-4YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'calendarYearReturn4Year'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearQuarterly5YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-QUARTERLY-5YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'calendarYearReturn5Year'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 80,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCalendarYearInceptionColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'CALENDAR_YEAR-INCEPTION',
      headerName: this.translateService.instant('products.inception-date'),
      headerComponentFramework: HeaderRendererComponent,
      sortable: true,
      suppressMovable: true,
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.performanceInceptionDate;
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.performanceInceptionDateStd;
      },
      comparator: createDateComparator(API_DATE_FORMAT),
      width: 135,
      cellClass: ['ag-cell-frk--highlightedTertiary'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeMonthlyYtdColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-MONTHLY-YTD',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_YTD'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.MONTHLY,
              params,
              'cummulativeReturnYearToDate'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: [
        'ag-cell-frk--monetary',
        'ag-cell-frk--highlightedTertiary',
        'ag-cell-frk--numeric',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeMonthly3MonthsColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-MONTHLY-3MTHS',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_3MTH'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.MONTHLY,
              params,
              'cummulativeReturn3Month'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeMonthly1YrColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-MONTHLY-1YR',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_1YR'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.addPercentSignforNjbest(
              this.getPerformanceData(
                this.configurationName,
                PerformanceView.MONTHLY,
                params,
                'cummulativeReturn1Year'
              ),
              isNjBest
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeMonthly3YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-MONTHLY-3YR',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_3YR'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'cummulativeReturn3Year'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeMonthly5YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-MONTHLY-5YR',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_5YR'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'cummulativeReturn5Year'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeMonthly10YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-MONTHLY-10YR',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_10YR'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'cummulativeReturn10Year'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeMonthlyInceptionColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-MONTHLY-INCEPTION',
      headerName: this.translateService.instant('products.sinceInception'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}PerfInceptionPPSS`,
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        if (
          this.isSuppressForInceptionValid(
            suppressDetails,
            product.shareClass?.performanceInceptionDateStd
          )
        ) {
          return EMDASH;
        } else {
          return this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'cummulativeReturnSinceInception'
          );
        }
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        let inceptionTemplate: string;

        if (
          this.isSuppressForInceptionValid(
            suppressDetails,
            product.shareClass?.performanceInceptionDateStd
          )
        ) {
          inceptionTemplate = inceptionTemplate = `
                        <span>
                            ${EMDASH}
                        </span>
                        <p class="small text-grey1">
                            ${product.shareClass?.performanceInceptionDate}
                        </p>
                    `;
        } else {
          const cummulativeReturnSinceInception = this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'cummulativeReturnSinceInception'
          );
          inceptionTemplate = `
                        <span>
                            ${this.addPercentSignforNjbest(
                              cummulativeReturnSinceInception,
                              isNjBest
                            )}
                        </span>
                        <p class="small text-grey1">
                            ${product.shareClass?.performanceInceptionDate}
                        </p>
                    `;
        }
        return inceptionTemplate;
      },
      width: 135,
      cellClass: [
        'ag-cell-frk--highlightedTertiary',
        'ag-cell-frk--numeric',
        'ag-cell-frk--date',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeQuarterly3MonthsColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-QUARTERLY-3MTHS',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_3MTH'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.QUARTERLY,
              params,
              'cummulativeReturn3Month'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeQuarterlyYtdColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-QUARTERLY-YTD',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_YTD'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.getPerformanceData(
              this.configurationName,
              PerformanceView.QUARTERLY,
              params,
              'cummulativeReturnYearToDate'
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      cellClass: [
        'ag-cell-frk--monetary',
        'ag-cell-frk--highlightedTertiary',
        'ag-cell-frk--numeric',
      ],
      width: 97,
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeQuarterly1YrColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-QUARTERLY-1YR',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_1YR'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.addPercentSignforNjbest(
              this.getPerformanceData(
                this.configurationName,
                PerformanceView.QUARTERLY,
                params,
                'cummulativeReturn1Year'
              ),
              isNjBest
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
      width: 97,
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeQuarterly3YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-QUARTERLY-3YR',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_3YR'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.QUARTERLY,
            params,
            'cummulativeReturn3Year'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeQuarterly5YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-QUARTERLY-5YR',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_5YR'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.QUARTERLY,
            params,
            'cummulativeReturn5Year'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeQuarterly10YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-QUARTERLY-10YR',
      headerName: this.translateService.instant('pds.CUMULATIVE_RET_10YR'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.QUARTERLY,
            params,
            'cummulativeReturn10Year'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getCumulativeQuarterlyInceptionColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'CUMULATIVE-QUARTERLY-INCEPTION',
      headerName: this.translateService.instant('products.sinceInception'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        isAlignRight: true,
        footnotePlacement: `PPSS:${this.getConfigurationNameForPlacement()}PerfInceptionPPSS`,
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        if (
          this.isSuppressForInceptionValid(
            suppressDetails,
            product.shareClass?.performanceInceptionDateStd
          )
        ) {
          return EMDASH;
        } else {
          return this.addPercentSignforNjbest(
            this.getPerformanceData(
              this.configurationName,
              PerformanceView.QUARTERLY,
              params,
              'cummulativeReturnSinceInception'
            ),
            isNjBest
          );
        }
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        let inceptionTemplate: string;

        if (
          this.isSuppressForInceptionValid(
            suppressDetails,
            product.shareClass?.performanceInceptionDateStd
          )
        ) {
          inceptionTemplate = `
                        <span>
                            ${EMDASH}
                        </span>
                        <p class="small text-grey1">
                            ${product.shareClass?.performanceInceptionDate}
                        </p>
                    `;
        } else {
          const cummulativeReturnSinceInception = this.getPerformanceData(
            this.configurationName,
            PerformanceView.QUARTERLY,
            params,
            'cummulativeReturnSinceInception'
          );
          inceptionTemplate = `
                        <span>
                            ${this.addPercentSignforNjbest(
                              cummulativeReturnSinceInception,
                              isNjBest
                            )}
                        </span>
                        <p class="small text-grey1">
                            ${product.shareClass?.performanceInceptionDate}
                        </p>
                    `;
        }
        return inceptionTemplate;
      },
      width: 135,
      cellClass: [
        'ag-cell-frk--highlightedTertiary',
        'ag-cell-frk--numeric',
        'ag-cell-frk--date',
      ],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteMonthly1YrColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-MONTHLY-1YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.addPercentSignforNjbest(
              this.getPerformanceData(
                this.configurationName,
                PerformanceView.MONTHLY,
                params,
                'discreteAnnualRollingYear1'
              ),
              isNjBest
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteMonthly2YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-MONTHLY-2YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'discreteAnnualRollingYear2'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteMonthly3YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-MONTHLY-3YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'discreteAnnualRollingYear3'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteMonthly4YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-MONTHLY-4YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.MONTHLY,
          params,
          'discreteAnnualRollingYear4'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteMonthly5YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-MONTHLY-5YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'discreteAnnualRollingYear5'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteMonthly10YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-MONTHLY-10YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.MONTHLY,
            params,
            'discreteAnnualRollingYear10'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteQuarterly1YrColDef(
    suppressDetails: SuppressDetails,
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-QUARTERLY-1YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.isSuppressForInceptionValid(
          suppressDetails,
          params.data.shareClass?.performanceInceptionDateStd
        )
          ? EMDASH
          : this.addPercentSignforNjbest(
              this.getPerformanceData(
                this.configurationName,
                PerformanceView.QUARTERLY,
                params,
                'discreteAnnualRollingYear1'
              ),
              isNjBest
            );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteQuarterly2YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-QUARTERLY-2YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'discreteAnnualRollingYear2'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteQuarterly3YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-QUARTERLY-3YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.QUARTERLY,
            params,
            'discreteAnnualRollingYear3'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteQuarterly4YrColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-QUARTERLY-4YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.getPerformanceData(
          this.configurationName,
          PerformanceView.QUARTERLY,
          params,
          'discreteAnnualRollingYear4'
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteQuarterly5YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-QUARTERLY-5YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.QUARTERLY,
            params,
            'discreteAnnualRollingYear5'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteQuarterly10YrColDef(
    alteredColDef?: ColDef,
    isNjBest?: boolean
  ): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-QUARTERLY-10YR',
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: { isAlignRight: true },
      valueGetter: (params) => {
        return this.addPercentSignforNjbest(
          this.getPerformanceData(
            this.configurationName,
            PerformanceView.QUARTERLY,
            params,
            'discreteAnnualRollingYear10'
          ),
          isNjBest
        );
      },
      sortable: true,
      suppressMovable: true,
      comparator: floatComparator,
      width: 97,
      cellClass: ['ag-cell-frk--highlightedTertiary', 'ag-cell-frk--numeric'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getDiscreteInceptionColDef(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'DISCRETE-INCEPTION',
      headerName: this.translateService.instant('products.inception-date'),
      headerComponentFramework: HeaderRendererComponent,
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.performanceInceptionDate;
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.performanceInceptionDateStd;
      },
      sortable: true,
      suppressMovable: true,
      comparator: createDateComparator(API_DATE_FORMAT),
      width: 135,
      cellClass: ['ag-cell-frk--highlightedTertiary'],
    };

    return merge(initialColDef, alteredColDef);
  }

  getAvg12MonthsAum(alteredColDef?: ColDef): ColDef {
    const initialColDef: ColDef = {
      colId: 'AVG12MonthAUM',
      headerName: this.translateService.instant(
        'products.ppss-avg-12-mnth-aum-value'
      ),
      headerComponentFramework: HeaderRendererComponent,
      suppressMovable: true,
      // NB: keep valueGetter function for use by card view
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.aum?.avg12MonthsAum;
      },
      cellRenderer: (params) => {
        const product: ProductData = params.data;
        return product.aum?.avg12MonthsAum;
      },
      sortable: true,
      comparator: floatComparator,
      width: 125,
    };

    return merge(initialColDef, alteredColDef);
  }

  // ***** AG-Grid Column Definitions Ends *****

  /**
   * Get the expected performance data for particular performance field.
   * @param configurationName product type configurationname
   * @param performanceView performance view type i.e, Monthly/Annually/etc
   * @param params ValueGetterParams
   * @param property performanceDetails property
   */
  getPerformanceData(
    configurationName: ConfigurationId,
    performanceView: PerformanceView,
    params: ValueGetterParams,
    property: string
  ) {
    const product: ProductData = params.data;
    const selectedCalcTypes = this.getSelectedCalcTypeValues(configurationName);
    let performanceDetail: PerformanceDetail[];

    if (performanceView === PerformanceView.MONTHLY) {
      performanceDetail = product.shareClass?.performance.monthEnd;
    } else if (performanceView === PerformanceView.QUARTERLY) {
      performanceDetail = product.shareClass?.performance.quarterEnd;
    }

    // NGC-7670 Mapping calcType data if calcTypeStd is SINGAPORE
    const performanceData = performanceDetail?.filter((detail) => {
      return detail.calcTypeStd === 'SINGAPORE'
        ? // intentionally checking "calcType" in case of SINGAPORE
          // Assuming PDS always supply calcType as "AACR"
          selectedCalcTypes.includes(detail.calcType.toUpperCase()) // Just to be sure AACR remains in Caps
        : selectedCalcTypes.includes(detail.calcTypeStd);
    });

    let value = EMDASH;
    if (performanceData?.length > 0) {
      // NGC-13646: SA - Performance currency calculation.
      // ********** LOGIC **********
      // Check first, if country code is SA.
      // If yes, check for selected performance currency (dropdown value at performance header).
      // When option is selected as 'SHARE_CLASS', performance to be extracted will be its base currency value (fund level currency).
      // Compare and select performance record which matches performance currency and extract value as per performance view.
      // This will work for default selection i.e., share class (primary) as well as the other currency option.
      // If no, use BAU.
      if (product.countryCode === 'ZA') {
        const selectedCurrencyCode =
          params.context.selectedPerformanceCurrency === 'SHARE_CLASS'
            ? product.currencyCode
            : params.context.selectedPerformanceCurrency;

        const perfAsPerShareClassCode: PerformanceDetail = performanceData.find(
          (data) => data.currencyCode === selectedCurrencyCode
        );

        if (perfAsPerShareClassCode) {
          value = perfAsPerShareClassCode[property];
        }
      } else {
        performanceData.sort(
          (first, second) =>
            selectedCalcTypes.indexOf(first.calcTypeStd) -
            selectedCalcTypes.indexOf(second.calcTypeStd)
        );
        value = performanceData[0][property];
      }
    }

    return value;
  }

  /**
   * Create column labels for given date.
   * @param perfView current performance view
   * @param asOfDateStd performanceAsOf date value
   * @param language language from decorated config
   */
  createColumnLabels(perfView: string, asOfDateStd: string, language: string) {
    const asOfMoment = moment(asOfDateStd, API_DATE_FORMAT, language);
    const labelObj = {};

    if (perfView === 'CALENDAR_YEAR') {
      let thisYear = asOfMoment.year();
      const thisMonth = asOfMoment.month();

      // COREWEB-3916 / COREWEB-3769: if month december, then need to add year
      if (thisMonth === 11) {
        thisYear++;
      }
      [1, 2, 3, 4, 5].forEach((yearsAgo) => {
        if (Number.isNaN(thisYear)) {
          labelObj[`calendar_year-${yearsAgo}yr`] = '—';
        } else {
          labelObj[`calendar_year-${yearsAgo}yr`] = thisYear - yearsAgo;
        }
      });
    } else if (perfView === 'DISCRETE') {
      [1, 2, 3, 4, 5].forEach((yearsAgo) => {
        if (!asOfMoment.isValid()) {
          labelObj[`discrete-${yearsAgo}yr`] = '—';
        } else {
          const startDate = asOfMoment
            .clone()
            .subtract(yearsAgo, 'years')
            .format('MMM-YY');
          const endDate = asOfMoment
            .clone()
            .subtract(yearsAgo - 1, 'years')
            .format('MMM-YY');
          const dateRange = `${startDate} / ${endDate}`;
          labelObj[`discrete-${yearsAgo}yr`] = dateRange;
        }
      });
    }

    return labelObj;
  }

  /**
   * Check if inception suppression could be valid or not.
   * Based upon 6 months/1 year suppression information from config, if present.
   * Calculate the fund inception period, shouldn't be less than 6 months/1 year.
   * @param suppressDetails suppress details from config
   * @param performanceInceptionDateStd performance inception date standard from config
   */
  isSuppressForInceptionValid(
    suppressDetails: SuppressDetails,
    performanceInceptionDateStd: string
  ): boolean {
    let isSuppressionValid = false;

    // LOGIC: NGC-1351.
    // If suppression for 1 year is applied, then there is no need to check for 6 months.
    // While, if 6 month is only applied, then 2nd condition will work accordingly.
    if (suppressDetails?.showMDashForUnder1Year) {
      isSuppressionValid = performanceInceptionDateStd > getCached1YearAgoStd();
    } else if (suppressDetails?.showMDashForUnder6Months) {
      isSuppressionValid =
        performanceInceptionDateStd > getCached6MonthsAgoStd();
    }

    return isSuppressionValid;
  }

  // ***** Grid Card helpers *****
  // TODO: some of these functions below are generic enough to be moved into shared library

  public getPpssCardData(
    columnDefs: (ColDef | ColGroupDef)[],
    fund: ProductData,
    gridContext?: any,
    isSiteIntl?: boolean
  ): PpssCard {
    const processedSections = this.processSections(
      columnDefs,
      fund,
      gridContext,
      isSiteIntl
    );

    // Remove Favs column from PPSS card in mobile and tablet
    processedSections.forEach((section) => {
      if (section.sectionId === 'FUND') {
        const itemIndex = section.items.findIndex(
          (item) => item.colId === 'FAV-FUND'
        );
        section.items.splice(itemIndex, 1);
      }
    });

    const card: PpssCard = {
      cardId: `${fund.fundId}-${fund.shareClass?.shareClassCode}`,
      fundDetail: this.getFundDetailToDisplay(fund, isSiteIntl),
      shareClassName: fund.shareClass.shareClassName,
      fundLink: fund.shareClass?.link,
      fundSectionLinks: fund.shareClass?.sectionLinks,
      sections: processedSections,
      ppssMessage: fund.ppssAlert?.ppssMessage,
      ppssTooltip: fund.ppssAlert?.ppssTooltip,
      showSalesCharges: false,
      maxIntialSalesCharge: fund.shareClass?.charges?.maxIntialSalesCharge,
      cdsc: fund.shareClass?.charges?.cdsc,
      performanceFeeCharge: fund.shareClass?.charges?.performanceFeeCharge,
      maintananceCharge: fund.shareClass?.charges?.maintananceCharge,
      showPerformanceAndMaintananceCharges: false,
      isSoftLaunch: fund.shareClass?.isSoftLaunch,
    };

    this.favoritesService
      .isFavorite$(card.cardId as FundShareClassId)
      .subscribe((isFav: boolean) => {
        card.isFavoriteFund = isFav;
      });

    const fundNameDef: ColDef = this.findColDefFromGroups(
      columnDefs,
      'FUND-NAME'
    );
    if (fundNameDef?.cellRendererParams?.showSalesCharges) {
      if (card.maxIntialSalesCharge !== EMDASH && card.cdsc !== EMDASH) {
        card.showSalesCharges = true;
      } else {
        card.showSalesCharges = false;
      }
    }
    if (fundNameDef?.cellRendererParams?.showPerformanceAndMaintananceCharges) {
      if (
        card.performanceFeeCharge !== EMDASH &&
        card.maintananceCharge !== EMDASH
      ) {
        card.showPerformanceAndMaintananceCharges = true;
      } else {
        card.showPerformanceAndMaintananceCharges = false;
      }
    }
    return card;
  }

  private findColDefFromGroups(
    columnDefs: (ColDef | ColGroupDef)[],
    id: string
  ): ColDef {
    let colDef: ColDef = null;
    columnDefs.forEach((colOrGroup: ColDef | ColGroupDef): void => {
      if (this.isColGroup(colOrGroup)) {
        colOrGroup.children?.forEach((childDef: ColDef): void => {
          if (childDef.colId === id) {
            colDef = childDef;
          }
        });
      } else {
        if (colOrGroup.colId === id) {
          colDef = colOrGroup;
        }
      }
    });
    return colDef;
  }

  // determines if definition is column or column group
  private isColGroup(def: ColDef | ColGroupDef): def is ColGroupDef {
    return (
      (def as ColGroupDef).groupId !== undefined ||
      (def as ColGroupDef).children !== undefined
    );
  }

  private splitMorningStarGroup(
    morningStarGroup: PpssCardSection
  ): PpssCardSection {
    // NB: this mutates (removes items from) the original section
    return {
      ...morningStarGroup,
      sectionTitle: '',
      sectionSubTitle: '',
      isExpandable: true,
      items: morningStarGroup.items.splice(2),
    };
  }

  private splitExpenseGroup(
    performanceGroup: PpssCardSection
  ): PpssCardSection {
    const expenseRatioIndex = performanceGroup.items.findIndex((keys) => {
      return keys.colId === 'EXPENSERATIO';
    });
    if (expenseRatioIndex > 0) {
      return {
        ...performanceGroup,
        sectionTitle: '',
        sectionSubTitle: '',
        isExpandable: true,
        items: performanceGroup.items.splice(expenseRatioIndex),
      };
    }
  }

  private processSections(
    columnDefs: (ColDef | ColGroupDef)[],
    fund: ProductData,
    gridContext?: any,
    isSiteIntl?: boolean
  ): PpssCardSection[] {
    const sections: PpssCardSection[] = [];
    const currentSection: PpssCardSection = {
      sectionId: '',
      items: [],
    };
    // let currentSection: PpssCardSection = null;
    // if columnDefs is instance of colGroupDef []
    if (columnDefs.length > 0 && this.isColGroup(columnDefs[0])) {
      columnDefs.forEach((def: ColDef | ColGroupDef): void => {
        if (this.isColGroup(def)) {
          // new group
          const newSection: PpssCardSection = this.processColGroup(
            def,
            fund,
            gridContext,
            isSiteIntl
          );
          sections.push(newSection);

          // If MorningStar group, split into 2 sections: 1 fixed and 1 expandable
          if (newSection.sectionId === 'MORNINGSTARRATINGS') {
            sections.push(this.splitMorningStarGroup(newSection));
          }
          // If Performance group, split into 2 sections to display alternative colors
          else if (newSection.sectionId === 'PERFORMANCE') {
            const expenseGroupSection = this.splitExpenseGroup(newSection);
            if (expenseGroupSection) {
              sections.push(expenseGroupSection);
            }
          }
        } else {
        }
      });
      // else if colDef is instance of colDef []
    } else {
      currentSection.items = this.processCols(
        columnDefs,
        fund,
        gridContext,
        isSiteIntl
      );
      sections.push(currentSection);
    }
    // logger.debug('processSections()', sections);
    return sections;
  }

  private processColGroup(
    groupDef: ColGroupDef,
    fund: ProductData,
    gridContext?: any,
    isSiteIntl?: boolean
  ): PpssCardSection {
    const section: PpssCardSection = {
      sectionId: groupDef.groupId || '',
      items: [],
    };
    if (groupDef.headerName) {
      section.sectionSubTitle = groupDef.headerName;
    } else if (groupDef.headerValueGetter) {
      if (typeof groupDef.headerValueGetter === 'string') {
        section.sectionSubTitle = groupDef.headerValueGetter;
      } else {
        section.sectionSubTitle = groupDef.headerValueGetter();
      }
    }
    if (groupDef.children) {
      section.items = this.processCols(
        groupDef.children,
        fund,
        gridContext,
        isSiteIntl
      );
    }
    // Performance and Charges groups will go in ExpandCollapse component
    section.isExpandable =
      !isSiteIntl && ['PERFORMANCE', 'CHARGES'].includes(section.sectionId);

    if (['NAV', 'ADDITIONAL_PERFORMANCE'].includes(section.sectionId)) {
      const titleKey: string =
        groupDef.headerGroupComponentParams?.headerLabelKey;
      if (titleKey) {
        section.sectionTitle = this.translateService.instant(titleKey);
      }
      const asOfDate: string = groupDef.headerGroupComponentParams?.asOfDateText();
      if (asOfDate) {
        section.sectionSubTitle = `${this.translateService.instant(
          'pds.PERF_AS_OF_DATE'
        )} ${asOfDate}`;
      }
    }

    if (section.sectionId === 'PERFORMANCE') {
      const performaceComponent: PerformanceComponent =
        groupDef.headerGroupComponentParams?.perfTable;
      if (performaceComponent) {
        const performanceView: PerformanceViewOption =
          performaceComponent.performanceView;
        if (performanceView) {
          let titleKey: string = performanceView.headerLabelKey;
          const selectedCalcType: string =
            typeof performaceComponent.calcType === 'string'
              ? performaceComponent.calcType
              : (performaceComponent.calcType as ToggleProps).selected;
          if (selectedCalcType) {
            titleKey = `${titleKey}-${selectedCalcType}`;
            section.sectionPlacement = `PPSS:${performaceComponent.productType.replace(
              /-|\s/g,
              ''
            )}TotalReturnsPerformance${upperFirst(
              camelCase(selectedCalcType)
            )}`;
          }
          section.sectionTitle = this.translateService.instant(titleKey);
        }

        section.sectionSubTitle = `${this.translateService.instant(
          'pds.PERF_AS_OF_DATE'
        )} ${performaceComponent.performanceAsOf}`;
      }
    }

    if (section.sectionId === 'MORNINGSTARRATINGS') {
      section.sectionTitle = this.translateService.instant(
        'products.ratings-title'
      );
      section.sectionPlacement = 'PPSS:MorningStarRatingPPSS';
      section.sectionToolTip = this.translateService.tooltip(
        'products.ratings-title'
      );
      const ratingsAsOfDate: string =
        groupDef.headerGroupComponentParams?.ratingsAsOfDate;
      if (ratingsAsOfDate) {
        section.sectionSubTitle = `${this.translateService.instant(
          'products.as-of'
        )} ${ratingsAsOfDate} ${
          !isSiteIntl
            ? this.translateService.instant(
                'products.ratings-risk-adjusted-returns'
              )
            : ''
        }`;
      }
    }

    return section;
  }

  private processCols(
    colDefs: ColDef[],
    fund: ProductData,
    gridContext?: any,
    isSiteIntl?: boolean
  ): PpssCardItem[] {
    // Filter out checkbox for now. Will need revisited when funds can be selected
    return colDefs
      .filter(
        (colDef: ColDef): boolean =>
          !['FUND-NAME', 'QUICK-LINKS', 'FUND-DOCUMENT', 'CHECK-BOX'].includes(
            colDef.colId
          )
      )
      .map(
        (colDef: ColDef): PpssCardItem =>
          this.processCol(colDef, fund, gridContext, isSiteIntl)
      );
  }

  private getCellClasses(colDef: ColDef, fund: ProductData): string[] {
    let cellClasses: string[] = [];
    if (typeof colDef.cellClass === 'string') {
      cellClasses.push(colDef.cellClass);
    } else if (Array.isArray(colDef.cellClass)) {
      cellClasses = colDef.cellClass;
    } else if (colDef.cellClass instanceof Function) {
      cellClasses = colDef.cellClass({
        data: fund,
      } as CellClassParams) as string[];
    }
    return cellClasses;
  }

  private getHighlightLevel(
    cellClasses: string[]
  ): 'primary' | 'secondary' | 'tertiary' {
    if (cellClasses.includes('ag-cell-frk--highlightedTertiary')) {
      return 'tertiary';
    }
    if (cellClasses.includes('ag-cell-frk--highlightedSecondary')) {
      return 'secondary';
    }
    if (cellClasses.includes('ag-cell-frk--highlightedPrimary')) {
      return 'primary';
    }
    return null;
  }

  private getValueDirection(cellClasses: string[]): 'gain' | 'static' | 'loss' {
    if (cellClasses.includes('ag-cell-frk--gain')) {
      return 'gain';
    }
    if (cellClasses.includes('ag-cell-frk--static')) {
      return 'static';
    }
    if (cellClasses.includes('ag-cell-frk--loss')) {
      return 'loss';
    }
    return null;
  }

  private getRatings(colDef: ColDef, fund: ProductData): Partial<PpssCardItem> {
    const ratingsCellData: RatingsCellData = colDef.cellRendererParams;
    const rating: number = parseNumber(
      get(fund, ratingsCellData.ratingsPath, '0')
    );
    const isHybrid: boolean = get(fund, ratingsCellData.hybridPath, false);
    const numberOfFunds: number = get(
      fund,
      ratingsCellData.numberOfFundsPath,
      null
    );
    let valueSmallText: string;
    if (numberOfFunds) {
      // small text for ratings page
      valueSmallText = `${numberOfFunds} ${this.translateService.instant(
        'products.funds-in-category'
      )}`;
    }
    if (rating > 0 && ratingsCellData.showStarLabels) {
      // small text for Morningstar Page
      valueSmallText = this.translateService.instant(
        `products.rating_${rating}-star`
      );
    }
    return {
      rating: !isHybrid && rating,
      hybridRating: isHybrid && rating,
      valueSmallText,
      ratingTheme: ratingsCellData.theme,
      tooltip:
        isHybrid &&
        this.translateService.instant('products.morningstar-tooltip'),
    };
  }

  private getNavChange(fund: ProductData): Partial<PpssCardItem> {
    return {
      val: fund.shareClass?.nav?.navChangePercentage,
      additionalVal: fund.shareClass?.nav?.navChangeValue,
    };
  }

  private getMarketPriceChange(fund: ProductData): Partial<PpssCardItem> {
    return {
      val: fund.shareClass?.nav?.marketPriceChangePercentage,
      additionalVal: fund.shareClass?.nav?.marketPriceChangeValue,
    };
  }

  private getExpenseRatio(fund: ProductData): Partial<PpssCardItem> {
    return {
      values: [
        {
          label: this.translateService.instant('products.gross-expense-ratio'),
          val: fund.shareClass?.charges?.expenseRatioGross || EMDASH,
        },
        {
          label: this.translateService.instant('products.net-expense-ratio'),
          val: fund.shareClass?.charges?.expenseRatioNet || EMDASH,
        },
      ],
    };
  }

  public processCol(
    colDef: ColDef,
    fund: ProductData,
    gridContext?: any,
    isSiteIntl?: boolean
  ): PpssCardItem {
    const cellClasses: string[] = this.getCellClasses(colDef, fund);
    const valueDirection = this.getValueDirection(cellClasses);
    const colId = colDef.colId || '';
    const hide: boolean = colDef.hide;

    let label = colDef.headerName || '';
    if (
      colDef.headerValueGetter &&
      colDef.headerValueGetter instanceof Function
    ) {
      label = colDef.headerValueGetter();
    }

    let val = '';
    if (colDef.valueGetter) {
      if (typeof colDef.valueGetter === 'string') {
        val = colDef.valueGetter;
      } else {
        val = colDef.valueGetter({
          data: fund,
          context: gridContext,
        } as ValueGetterParams);
      }
    }

    let valueSmallText = '';
    let valueSmallTheme: 'basic' | 'dark' | 'light' = 'basic';
    if (this.showPerformanceInceptionWithDate(colDef, fund, isSiteIntl)) {
      valueSmallText = fund.shareClass?.performanceInceptionDate;
      valueSmallTheme = 'light';
    }

    // WDE-2036 : Dates for 52-week high and 52-week low font will be smaller
    if (colDef.colId === '52WEEKHIGH') {
      val = fund.shareClass?.nav?.highestNavValueIn52Weeks;
      valueSmallText = `${this.translateService.instant('products.on')} ${
        fund.shareClass?.nav?.highestNavValueDateIn52Weeks
      }`;
      valueSmallTheme = 'light';
    }
    if (colDef.colId === '52WEEKLOW') {
      val = fund.shareClass?.nav?.lowestNavValueIn52Weeks;
      valueSmallText = `${this.translateService.instant('products.on')} ${
        fund.shareClass?.nav?.lowestNavValueDateIn52Weeks
      }`;
      valueSmallTheme = 'light';
    }

    // optional footnote
    const caveatPlacement: string =
      colDef.headerComponentParams?.footnotePlacement;

    // optional tooltip
    const tooltip: string = colDef.headerComponentParams?.tooltipContent;

    // ** NGC-3803 ** FIX: display perf. formatted date instead of std.
    if (
      colDef.colId.match('DISCRETE-INCEPTION') ||
      colDef.colId.match('CALENDAR_YEAR-INCEPTION') ||
      colDef.colId.match('AVG_ANNUAL-INCEPTION')
    ) {
      val = fund.shareClass?.performanceInceptionDate;
    }

    let item: PpssCardItem = {
      colId,
      hide,
      label,
      val,
      valueDirection,
      valueSmallText,
      valueSmallTheme,
      labelSmall: true,
      caveatPlacement,
      tooltip,
    };

    // ratings
    if (
      [
        'MORNINGSTAR-OVERALL',
        'MORNINGSTAR-RATING3YR',
        'MORNINGSTAR-RATING5YR',
        'MORNINGSTAR-RATING10YR',
      ].includes(colDef.colId)
    ) {
      item = {
        ...item,
        ...this.getRatings(colDef, fund),
      };
      item.rating || item.hybridRating ? (item.val = '') : (item.val = EMDASH);
    }

    // NAV Change
    if (colDef.colId === 'NAVCHANGEVALUE') {
      item = {
        ...item,
        ...this.getNavChange(fund),
      };
    }

    if (colDef.colId === 'MARKETPRICECHANGEVALUE') {
      item = {
        ...item,
        ...this.getMarketPriceChange(fund),
      };
    }

    // Expense ratio
    if (colDef.colId === 'EXPENSERATIO') {
      item = {
        ...item,
        val: null, // hide main val
        ...this.getExpenseRatio(fund),
      };
    }

    return item;
  }

  private showPerformanceInceptionWithDate(
    colDef: ColDef,
    fund: ProductData,
    isSiteIntl?: boolean
  ) {
    const hideDate = !isSiteIntl && fund.productType === PRODUCT_TYPE_ETF;
    return (
      (colDef.colId.match('AVG_ANNUAL-MONTHLY-INCEPTION') && !hideDate) ||
      (colDef.colId.match('AVG_ANNUAL-QUARTERLY-INCEPTION') && !hideDate) ||
      colDef.colId.match('CUMULATIVE-MONTHLY-INCEPTION') ||
      colDef.colId.match('CUMULATIVE-QUARTERLY-INCEPTION')
    );
  }

  private addPercentSignforNjbest(input: string, isNjbest?: boolean): string {
    if (input === EMDASH) {
      return input;
    }
    if (input && isNjbest) {
      // For NJBest input will be with '%'.
      return `${input}%`;
    }
    // For regular pages we need to return input without changes.
    return input;
  }
}
